import { useEffect, useRef } from 'react'
import { useApolloClient } from '@graphcommerce/graphql'
import { useCustomerSession } from '@graphcommerce/magento-customer/hooks'
import { CustomerTokenDocument } from '@graphcommerce/magento-customer/hooks/CustomerToken.gql'
import { useRouter } from 'next/router'

const TOKEN_EXPIRATION_MS = 60 * 60 * 1000 // 60 minutes
const ACTIVITY_THRESHOLD_MS = 5 * 60 * 1000 // Only extend if more than 5 minutes old
const EXTEND_BY_MS = 30 * 60 * 1000 // Extend by 30 minutes on activity

/**
 * Hook that extends the session when user is active
 * This prevents logout for users who are actively browsing
 */
export function useExtendSessionOnActivity() {
  const session = useCustomerSession()
  const client = useApolloClient()
  const router = useRouter()
  const lastExtendedRef = useRef<number>(0)
  const activityTimeoutRef = useRef<NodeJS.Timeout>()

  const extendSession = () => {
    if (!session.loggedIn || !session.createdAt || !session.token) return

    const now = Date.now()
    const createdAt = new Date(session.createdAt).getTime()
    const sessionAge = now - createdAt
    const timeSinceLastExtend = now - lastExtendedRef.current

    // Only extend if:
    // 1. Session is older than threshold (not just created)
    // 2. Haven't extended recently (prevent spam)
    // 3. Session isn't already expired
    if (
      sessionAge > ACTIVITY_THRESHOLD_MS &&
      timeSinceLastExtend > ACTIVITY_THRESHOLD_MS &&
      sessionAge < TOKEN_EXPIRATION_MS
    ) {
      console.log('🔄 Extending session due to user activity...')
      
      // Update createdAt to extend the session
      const newCreatedAt = new Date(now - (TOKEN_EXPIRATION_MS - EXTEND_BY_MS)).toUTCString()
      
      client.writeQuery({
        query: CustomerTokenDocument,
        data: {
          customerToken: {
            __typename: 'CustomerToken',
            token: session.token,
            createdAt: newCreatedAt,
            valid: true,
          },
        },
        broadcast: true,
      })

      lastExtendedRef.current = now
      console.log(`✅ Session extended by ${EXTEND_BY_MS / 60000} minutes`)
    }
  }

  const handleActivity = () => {
    // Debounce activity events
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current)
    }

    activityTimeoutRef.current = setTimeout(() => {
      extendSession()
    }, 1000) // Wait 1 second after last activity
  }

  useEffect(() => {
    if (!session.loggedIn) {
      lastExtendedRef.current = 0
      return
    }

    // Listen for user activity
    const events = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ]

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current)
      }
    }
  }, [session.loggedIn, session.createdAt, session.token])

  // Also extend on route changes (navigation)
  useEffect(() => {
    const handleRouteChange = () => {
      extendSession()
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [session.loggedIn, session.createdAt, session.token, router])
}
